import { ref } from 'vue'
import { initNativeNetwork } from '@/native/network'

// Shared PWA state: update available, offline, installable, post-install onboarding.
const needRefresh = ref(false)
const offline = ref(typeof navigator !== 'undefined' && !navigator.onLine)
const installable = ref(false)
const showOnboarding = ref(false)
let waitingWorker = null
let deferredInstall = null
let reloading = false
let networkOffline = offline.value
let backendOffline = false

/** Combines transport and backend reachability into the single UI offline state. */
function refreshOfflineState () {
  offline.value = networkOffline || backendOffline
}

/** Aktualisiert den Offline-Indikator anhand der Backend-Erreichbarkeit. */
function setOffline (value) {
  backendOffline = value
  refreshOfflineState()
}

/** Aktualisiert den Offline-Indikator anhand der Netzwerkverbindung. */
function setNetworkOffline (value) {
  networkOffline = value
  refreshOfflineState()
}

/**
 * Hinterlegt einen wartenden Service Worker und setzt `needRefresh`, sobald ein Update bereitsteht.
 * @param {ServiceWorkerRegistration} registration - Die SW-Registrierung.
 */
function setWaiting (registration) {
  waitingWorker = registration?.waiting || null
  needRefresh.value = !!waitingWorker
}

/**
 * Aktiviert ein bereitstehendes Update: weist den wartenden Worker an, sofort zu übernehmen
 * (`SKIP_WAITING`); ohne wartenden Worker wird einfach neu geladen.
 */
function updateApp () {
  if (!waitingWorker) { window.location.reload(); return }
  waitingWorker.postMessage({ type: 'SKIP_WAITING' })
}

/**
 * Zeigt den nativen Installations-Dialog der PWA (sofern zuvor `beforeinstallprompt` kam).
 * @returns {Promise<boolean>} `true`, wenn der Nutzer die Installation akzeptiert hat.
 */
async function install () {
  if (!deferredInstall) return false
  deferredInstall.prompt()
  const { outcome } = await deferredInstall.userChoice
  deferredInstall = null
  installable.value = false
  return outcome === 'accepted'
}

/**
 * Prüft, ob die App als installierte PWA (standalone) läuft – inklusive iOS-Sonderfall.
 * @returns {boolean} `true`, wenn im Standalone-Modus.
 */
function isStandalone () {
  return window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
}

/** Schließt das Onboarding und merkt sich dauerhaft, dass es bereits gezeigt wurde. */
function dismissOnboarding () {
  showOnboarding.value = false
  localStorage.setItem('ams-onboarded', '1')
  localStorage.removeItem('ams-onboard-pending')
}

/**
 * Registriert Periodic Background Sync ('ams-refresh', stündlich), um Flug-/Stressdaten im
 * Hintergrund warm zu halten. Feature-detektiert und berechtigungsgebunden: für installierte
 * PWAs automatisch erteilt, ohne je einen Dialog zu zeigen.
 * @returns {Promise<void>}
 */
async function registerPeriodicSync () {
  if (!('serviceWorker' in navigator)) return
  try {
    const reg = await navigator.serviceWorker.ready
    if (!('periodicSync' in reg)) return
    const status = await navigator.permissions.query({ name: 'periodic-background-sync' })
    if (status.state === 'granted') {
      await reg.periodicSync.register('ams-refresh', { minInterval: 60 * 60 * 1000 })
    }
  } catch (_) { /* unsupported browser – progressive enhancement */ }
}

/**
 * Initialisiert die PWA nach dem Mounten: registriert Background Sync, koppelt den
 * Offline-Indikator (nativ über Capacitor-Network) und entscheidet, ob das Onboarding
 * angezeigt wird. Einmalig aufrufen.
 */
function initPwa () {
  registerPeriodicSync()
  // Native shells: drive the offline indicator from @capacitor/network (more reliable
  // than navigator.onLine). No-op on the web, where the online/offline events below apply.
  initNativeNetwork(setNetworkOffline)
  const onboarded = localStorage.getItem('ams-onboarded') === '1'
  const pending = localStorage.getItem('ams-onboard-pending') === '1'
  if (!onboarded && (pending || isStandalone())) showOnboarding.value = true
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { setNetworkOffline(false) })
  window.addEventListener('offline', () => { setNetworkOffline(true) })
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferredInstall = e
    installable.value = true
  })
  window.addEventListener('appinstalled', () => {
    installable.value = false
    if (localStorage.getItem('ams-onboarded') !== '1') {
      localStorage.setItem('ams-onboard-pending', '1')
      showOnboarding.value = true
    }
  })
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return
      reloading = true
      window.location.reload()
    })
  }
}

/**
 * Composable für den PWA-Lebenszyklus: Update-Verfügbarkeit, Offline-Status, Installierbarkeit
 * und das Onboarding nach der Installation. Der Zustand ist modulweit geteilt; die globalen
 * Browser-Events (online/offline, beforeinstallprompt, appinstalled, controllerchange) werden
 * beim Import einmalig registriert.
 *
 * @returns {{
 *   needRefresh: import('vue').Ref<boolean>,
 *   offline: import('vue').Ref<boolean>,
 *   installable: import('vue').Ref<boolean>,
 *   showOnboarding: import('vue').Ref<boolean>,
 *   setWaiting: (registration: ServiceWorkerRegistration) => void,
 *   updateApp: () => void,
 *   install: () => Promise<boolean>,
 *   dismissOnboarding: () => void,
 *   initPwa: () => void
 * }} Reaktiver PWA-Zustand und Aktionen.
 */
export function usePwa () {
  return { needRefresh, offline, installable, showOnboarding, isStandalone, setOffline, setNetworkOffline, setWaiting, updateApp, install, dismissOnboarding, initPwa }
}
